import type { LifeSummary } from '@gamengayxua/shared';

export type CardAspectRatio = '1:1' | '9:16';

/**
 * Renders the Life Summary card to an offscreen HTML Canvas and returns data URL / blob
 */
export async function generateLifeCardCanvas(
  summary: LifeSummary,
  ratio: CardAspectRatio = '1:1'
): Promise<HTMLCanvasElement> {
  const isSquare = ratio === '1:1';
  const width = 1080;
  const height = isSquare ? 1080 : 1920;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Cannot get 2D canvas context');

  // Helper drawing functions
  const drawRoundedRect = (
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    fill?: string,
    stroke?: string,
    strokeWidth: number = 1
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = strokeWidth;
      ctx.stroke();
    }
  };

  // 1. BACKGROUND GRADIENT
  const bgGrad = ctx.createLinearGradient(0, 0, width, height);
  bgGrad.addColorStop(0, '#070b16');
  bgGrad.addColorStop(0.5, '#0b1326');
  bgGrad.addColorStop(1, '#050811');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // Subtle radial glow at top
  const radialGlow = ctx.createRadialGradient(width / 2, 0, 10, width / 2, 0, 600);
  radialGlow.addColorStop(0, 'rgba(0, 229, 255, 0.12)');
  radialGlow.addColorStop(0.6, 'rgba(245, 158, 11, 0.05)');
  radialGlow.addColorStop(1, 'transparent');
  ctx.fillStyle = radialGlow;
  ctx.fillRect(0, 0, width, height);

  // Outer Decorative Borders
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  ctx.lineWidth = 2;
  ctx.strokeRect(28, 28, width - 56, height - 56);
  ctx.strokeStyle = 'rgba(245, 158, 11, 0.25)';
  ctx.lineWidth = 1;
  ctx.strokeRect(34, 34, width - 68, height - 68);

  // 2. HEADER: BRAND & DOCUMENT ID
  const headerY = isSquare ? 58 : 80;
  ctx.textAlign = 'left';
  ctx.fillStyle = '#f59e0b';
  ctx.font = 'bold 26px "Be Vietnam Pro", sans-serif';
  ctx.fillText('MỘT KIẾP NGƯỜI • HỒ SƠ NHÂN SINH', 60, headerY);

  ctx.fillStyle = '#94a3b8';
  ctx.font = '500 17px "Be Vietnam Pro", sans-serif';
  ctx.fillText('Mô phỏng nhân khẩu học chuẩn UN WPP 2024 & World Bank', 60, headerY + 26);

  // Code badge on top right
  const shareCode = summary.shareCode;
  ctx.textAlign = 'right';
  drawRoundedRect(width - 300, headerY - 20, 240, 42, 10, 'rgba(15, 23, 42, 0.9)', 'rgba(0, 229, 255, 0.4)', 1.5);
  ctx.fillStyle = '#00e5ff';
  ctx.font = 'bold 18px "Share Tech Mono", monospace';
  ctx.fillText(`#${shareCode}`, width - 75, headerY + 8);

  // Divider
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(60, headerY + 44);
  ctx.lineTo(width - 60, headerY + 44);
  ctx.stroke();

  // 3. MAIN CHARACTER SHOWCASE BLOCK
  const heroY = headerY + (isSquare ? 50 : 70);
  const heroH = isSquare ? 155 : 220;
  drawRoundedRect(60, heroY, width - 120, heroH, 18, 'rgba(17, 26, 48, 0.85)', 'rgba(245, 158, 11, 0.4)', 1.5);

  // Helper to draw procedural retro pixel portrait
  const drawPixelAvatar = (
    avX: number,
    avY: number,
    avSize: number,
    gender: 'male' | 'female',
    age: number
  ) => {
    const pSize = avSize / 12;
    const isOld = age >= 60;

    // Avatar background
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(avX, avY, avSize, avSize);

    // Hair
    const hairColor = isOld ? '#e2e8f0' : (age > 45 ? '#64748b' : '#334155');
    ctx.fillStyle = hairColor;
    for (let c = 3; c <= 8; c++) ctx.fillRect(avX + c * pSize, avY + 2 * pSize, pSize, pSize);
    for (let c = 2; c <= 9; c++) ctx.fillRect(avX + c * pSize, avY + 3 * pSize, pSize, pSize);
    ctx.fillRect(avX + 2 * pSize, avY + 4 * pSize, pSize, pSize);
    ctx.fillRect(avX + 9 * pSize, avY + 4 * pSize, pSize, pSize);
    if (gender === 'female') {
      ctx.fillRect(avX + 2 * pSize, avY + 5 * pSize, pSize, pSize);
      ctx.fillRect(avX + 9 * pSize, avY + 5 * pSize, pSize, pSize);
      ctx.fillRect(avX + 2 * pSize, avY + 6 * pSize, pSize, pSize);
      ctx.fillRect(avX + 9 * pSize, avY + 6 * pSize, pSize, pSize);
    }

    // Skin
    ctx.fillStyle = '#fed7aa';
    for (let r = 4; r <= 8; r++) {
      for (let c = 3; c <= 8; c++) {
        ctx.fillRect(avX + c * pSize, avY + r * pSize, pSize, pSize);
      }
    }

    // Eyes
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(avX + 4 * pSize, avY + 5 * pSize, pSize, pSize);
    ctx.fillRect(avX + 7 * pSize, avY + 5 * pSize, pSize, pSize);

    // Mouth / Smile
    ctx.fillStyle = '#f43f5e';
    ctx.fillRect(avX + 5 * pSize, avY + 7 * pSize, 2 * pSize, pSize);

    // Clothes
    ctx.fillStyle = gender === 'male' ? '#0ea5e9' : '#ec4899';
    for (let c = 2; c <= 9; c++) {
      ctx.fillRect(avX + c * pSize, avY + 9 * pSize, pSize, pSize);
      ctx.fillRect(avX + c * pSize, avY + 10 * pSize, pSize, pSize);
    }

    // Border
    ctx.strokeStyle = 'rgba(245, 158, 11, 0.7)';
    ctx.lineWidth = 2;
    ctx.strokeRect(avX, avY, avSize, avSize);
  };

  // Render Pixel Avatar inside Hero Block
  const avatarSize = isSquare ? 84 : 96;
  const avatarX = 85;
  const avatarY = heroY + (heroH - avatarSize) / 2;
  drawPixelAvatar(avatarX, avatarY, avatarSize, summary.gender, summary.deathAge);

  const textLeftX = avatarX + avatarSize + 20;

  // Full Name with Saint Name (Tên thánh & Họ tên)
  const saintName = summary.saintName ? `${summary.saintName} ` : '';
  const fullName = `${saintName}${summary.characterName}`;
  ctx.textAlign = 'left';
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 36px "Be Vietnam Pro", sans-serif';
  ctx.fillText(fullName, textLeftX, heroY + (isSquare ? 46 : 56));

  // Gender & Country Info
  ctx.fillStyle = '#38bdf8';
  ctx.font = '600 20px "Be Vietnam Pro", sans-serif';
  const genderVi = summary.gender === 'male' ? 'Nam' : 'Nữ';
  ctx.fillText(`${genderVi} • ${summary.country.flagEmoji} ${summary.country.nameVi} • Nơi sinh: ${summary.regionNameVi}`, textLeftX, heroY + (isSquare ? 82 : 98));

  // Origin Family Class & Birth Year
  ctx.fillStyle = '#cbd5e1';
  ctx.font = '500 17px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`Năm sinh: ${summary.birthYear} • Xuất thân: ${summary.familyClassVi}`, textLeftX, heroY + (isSquare ? 116 : 138));

  // In 9:16 vertical mode, add additional patron saint line
  if (!isSquare && summary.faithLife?.patronSaintVi) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = 'italic 17px "Be Vietnam Pro", sans-serif';
    ctx.fillText(`✝ Bổn mạng: ${summary.faithLife.patronSaintVi}`, textLeftX, heroY + 175);
  }

  // Giant Death Age Circle / Badge on the right
  const badgeX = width - 195;
  const badgeY = heroY + (heroH / 2) - 52;
  drawRoundedRect(badgeX, badgeY, 115, 104, 16, 'rgba(10, 17, 36, 0.95)', 'rgba(245, 158, 11, 0.65)', 2);
  ctx.textAlign = 'center';
  ctx.fillStyle = '#94a3b8';
  ctx.font = 'bold 13px "Be Vietnam Pro", sans-serif';
  ctx.fillText('HƯỞNG THỌ', badgeX + 57, badgeY + 25);
  ctx.fillStyle = '#f59e0b';
  ctx.font = 'bold 44px "Rajdhani", sans-serif';
  ctx.fillText(`${summary.deathAge}`, badgeX + 57, badgeY + 68);
  ctx.fillStyle = '#cbd5e1';
  ctx.font = '600 14px "Be Vietnam Pro", sans-serif';
  ctx.fillText('tuổi', badgeX + 57, badgeY + 92);

  // 4. BLOCK: CATHOLIC FAITH & VIRTUES (BẢN SẮC CÔNG GIÁO NHẸ NHÀNG)
  const faithY = heroY + heroH + (isSquare ? 14 : 24);
  const faithH = isSquare ? 144 : 230;
  drawRoundedRect(60, faithY, width - 120, faithH, 18, 'rgba(15, 23, 42, 0.85)', 'rgba(0, 229, 255, 0.3)', 1.5);

  ctx.textAlign = 'left';
  ctx.fillStyle = '#00e5ff';
  ctx.font = 'bold 21px "Be Vietnam Pro", sans-serif';
  ctx.fillText('✝ ĐỜI SỐNG ĐỨC TIN & DI SẢN CÔNG GIÁO', 90, faithY + 34);

  // Sacraments (Các Bí Tích) - TUÂN THỦ NGHIÊM NGẶT VIẾT HOA "Bí Tích"
  const sacraments = summary.faithLife?.sacramentsReceived || [
    'Bí Tích Rửa Tội',
    'Bí Tích Thánh Thể',
    'Bí Tích Thêm Sức',
    'Bí Tích Hôn Phối',
    'Bí Tích Xức Dầu Bệnh Nhân',
  ];

  ctx.fillStyle = '#e2e8f0';
  ctx.font = 'bold 17px "Be Vietnam Pro", sans-serif';
  ctx.fillText('Các Bí Tích đã lãnh nhận:', 90, faithY + 68);

  // Draw Sacraments pills
  let pillX = 90;
  const pillY = faithY + 80;
  for (const s of sacraments) {
    ctx.font = 'bold 15px "Be Vietnam Pro", sans-serif';
    const textWidth = ctx.measureText(s).width;
    if (pillX + textWidth + 24 > width - 90) break;
    drawRoundedRect(pillX, pillY, textWidth + 22, 30, 8, 'rgba(0, 229, 255, 0.14)', 'rgba(0, 229, 255, 0.4)', 1);
    ctx.fillStyle = '#38bdf8';
    ctx.fillText(s, pillX + 11, pillY + 21);
    pillX += textWidth + 30;
  }

  // Virtues and charity
  const virtueText = summary.faithLife?.charityAndVirtues?.[0] || 'Giữ vững niềm tin cậy mến, trung kiên vun đắp gia đình thánh thiện';
  ctx.fillStyle = '#cbd5e1';
  ctx.font = 'italic 17px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`“${virtueText}”`, 90, faithY + (isSquare ? 128 : 155));

  if (!isSquare && summary.faithLife?.charityAndVirtues?.[1]) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = '16px "Be Vietnam Pro", sans-serif';
    ctx.fillText(`• ${summary.faithLife.charityAndVirtues[1]}`, 90, faithY + 192);
  }

  // 5. BLOCK: CAREER, EDUCATION & FAMILY
  const infoY = faithY + faithH + (isSquare ? 14 : 24);
  const infoH = isSquare ? 152 : 230;
  drawRoundedRect(60, infoY, width - 120, infoH, 18, 'rgba(15, 23, 42, 0.85)', 'rgba(255, 255, 255, 0.12)', 1.5);

  ctx.fillStyle = '#f59e0b';
  ctx.font = 'bold 21px "Be Vietnam Pro", sans-serif';
  ctx.fillText('📋 HỌC VẤN, SỰ NGHIỆP & GIA ĐÌNH', 90, infoY + 34);

  const colWidth = (width - 180) / 3;
  // Col 1: Education
  ctx.fillStyle = '#94a3b8';
  ctx.font = '15px "Be Vietnam Pro", sans-serif';
  ctx.fillText('Trình độ học vấn:', 90, infoY + 66);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 18px "Be Vietnam Pro", sans-serif';
  ctx.fillText(summary.educationVi || 'Cử nhân Đại học', 90, infoY + 92);

  // Col 2: Career
  ctx.fillStyle = '#94a3b8';
  ctx.font = '15px "Be Vietnam Pro", sans-serif';
  ctx.fillText('Nghề nghiệp chính:', 90 + colWidth, infoY + 66);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 18px "Be Vietnam Pro", sans-serif';
  ctx.fillText(summary.finalCareer || 'Chuyên viên kỹ thuật', 90 + colWidth, infoY + 92);

  // Col 3: Family
  ctx.fillStyle = '#94a3b8';
  ctx.font = '15px "Be Vietnam Pro", sans-serif';
  ctx.fillText('Hôn nhân & con cái:', 90 + colWidth * 2, infoY + 66);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 18px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`${summary.finalRelationshipStatus || 'Đã kết hôn'} (${summary.childrenCount || 2} con)`, 90 + colWidth * 2, infoY + 92);

  // Cause of Death Banner inside block
  const causeY = infoY + (isSquare ? 116 : 155);
  drawRoundedRect(85, causeY - 14, width - 170, 36, 8, 'rgba(239, 68, 68, 0.14)', 'rgba(239, 68, 68, 0.35)', 1);
  ctx.fillStyle = '#fda4af';
  ctx.font = 'bold 16px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`✟ Nguyên nhân qua đời: ${summary.causeOfDeathVi || 'Tuổi cao sức yếu, tạ thế bình yên'}`, 100, causeY + 10);

  // 6. BLOCK: LIFE MAP (100-YEAR GRID BLOCKS INSPIRED BY 1in8b.life)
  const mapY = infoY + infoH + (isSquare ? 14 : 24);
  const mapH = isSquare ? 146 : 280;
  drawRoundedRect(60, mapY, width - 120, mapH, 18, 'rgba(15, 23, 42, 0.85)', 'rgba(255, 255, 255, 0.12)', 1.5);

  ctx.fillStyle = '#10b981';
  ctx.font = 'bold 21px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`🗺️ LIFE MAP • BẢN ĐỒ ${summary.deathAge} NĂM CUỘC ĐỜI (1 Ô = 1 NĂM)`, 90, mapY + 34);

  // Draw 100 Life Year Boxes Grid (20 cols x 5 rows)
  const gridStartX = 90;
  const gridStartY = mapY + 50;
  const cols = 20;
  const rows = 5;
  const boxW = (width - 180 - (cols - 1) * 5) / cols; // ~40px
  const boxH = isSquare ? 11 : 16;
  const gap = 5;

  const getYearColor = (y: number) => {
    if (y <= 5) return '#10b981';   // Childhood
    if (y <= 12) return '#0ea5e9';  // Primary
    if (y <= 18) return '#3b82f6';  // Adolescence
    if (y <= 25) return '#06b6d4';  // Youth
    if (y <= 40) return '#f59e0b';  // Adulthood
    if (y <= 60) return '#ea580c';  // Middle Age
    if (y <= 80) return '#8b5cf6';  // Senior
    return '#f43f5e';               // Centenarian
  };

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const yearIndex = r * cols + c + 1;
      const curX = gridStartX + c * (boxW + gap);
      const curY = gridStartY + r * (boxH + gap);

      if (yearIndex <= summary.deathAge) {
        // Lived year: filled with stage color
        drawRoundedRect(curX, curY, boxW, boxH, 3, getYearColor(yearIndex));
      } else {
        // Unlived year: faint outline
        drawRoundedRect(curX, curY, boxW, boxH, 3, 'rgba(30, 41, 59, 0.5)', 'rgba(255, 255, 255, 0.08)', 1);
      }
    }
  }

  // Legend bar under grid
  const legendY = gridStartY + rows * (boxH + gap) + 12;
  ctx.font = isSquare ? '500 11.5px "Be Vietnam Pro", sans-serif' : '500 13px "Be Vietnam Pro", sans-serif';
  ctx.fillStyle = '#94a3b8';
  ctx.fillText('🍼 0-5 Thơ Ấu  •  🎒 6-12 Đi Học  •  🌱 13-18 Thiếu Niên  •  🎓 19-25 Tuổi Trẻ  •  💼 26-40 Lập Nghiệp  •  🍂 41-60 Trung Niên  •  🍵 61-80 Tuổi Già  •  🕊️ 81+ Đại Thọ', 90, legendY + 10);

  // Key Decisions summary in 9:16 vertical mode
  if (!isSquare && summary.keyDecisions && summary.keyDecisions.length > 0) {
    const decY = legendY + 36;
    ctx.textAlign = 'left';
    ctx.fillStyle = '#cbd5e1';
    ctx.font = '16px "Be Vietnam Pro", sans-serif';
    const sampleDecisions = summary.keyDecisions.slice(-2);
    sampleDecisions.forEach((d, idx) => {
      ctx.fillText(`• ${d.age} tuổi: ${d.eventTitle} → ${d.choiceText}`, 90, decY + idx * 26);
    });
  }

  // 7. BLOCK: DEMOGRAPHICS PERCENTILE COMPARISON (UN WPP 2024)
  const demoY = mapY + mapH + (isSquare ? 14 : 24);
  const demoH = isSquare ? 180 : 270;
  drawRoundedRect(60, demoY, width - 120, demoH, 18, 'rgba(15, 23, 42, 0.9)', 'rgba(245, 158, 11, 0.35)', 1.5);

  ctx.textAlign = 'left';
  ctx.fillStyle = '#f59e0b';
  ctx.font = 'bold 21px "Be Vietnam Pro", sans-serif';
  ctx.fillText('📊 CHỈ SỐ NHÂN KHẨU HỌC & SO SÁNH QUỐC GIA', 90, demoY + 34);

  // National comparison numbers
  ctx.fillStyle = '#cbd5e1';
  ctx.font = '16px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`Tuổi thọ TB tại ${summary.country.nameVi}: ${summary.nationalLifeExpectancy.toFixed(1)} tuổi`, 90, demoY + 66);

  const diffText = summary.comparisonWithNationalAvg >= 0
    ? `+${summary.comparisonWithNationalAvg} năm so với mặt bằng chung (Sống thọ)`
    : `${summary.comparisonWithNationalAvg} năm so với mặt bằng chung`;
  ctx.fillStyle = summary.comparisonWithNationalAvg >= 0 ? '#34d399' : '#f87171';
  ctx.font = 'bold 16px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`Chênh lệch: ${diffText}`, 90, demoY + 92);

  // Percentile Progress Bar
  const barY = demoY + 114;
  const barW = width - 180;
  drawRoundedRect(90, barY, barW, 20, 10, 'rgba(30, 41, 59, 0.8)', 'rgba(255, 255, 255, 0.1)', 1);
  const fillW = Math.max(24, (barW * summary.percentileRank) / 100);
  drawRoundedRect(90, barY, fillW, 20, 10, '#f59e0b');

  ctx.fillStyle = '#fef08a';
  ctx.font = 'bold 16px "Be Vietnam Pro", sans-serif';
  ctx.fillText(`Phân vị tuổi thọ: Sống thọ hơn ${summary.percentileRank}% dân số cùng quốc gia`, 90, barY + 42);

  if (!isSquare) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = 'italic 16px "Be Vietnam Pro", sans-serif';
    ctx.fillText('*(Xác suất phân bổ theo số ca sinh thực tế và bảng phân vị UN WPP 2024)*', 90, barY + 80);
  }

  // 8. FOOTER / WATERMARK BRANDING
  const footerY = height - (isSquare ? 35 : 45);
  ctx.textAlign = 'left';
  ctx.fillStyle = '#94a3b8';
  ctx.font = '500 16px "Be Vietnam Pro", sans-serif';
  ctx.fillText('Khám phá kiếp sống của bạn tại @Gamengayxua_bot • Cổng Game Ngày Xưa', 60, footerY);

  ctx.textAlign = 'right';
  ctx.fillStyle = '#00e5ff';
  ctx.font = 'bold 16px "Share Tech Mono", monospace';
  ctx.fillText('https://gamengayxua.pages.dev', width - 60, footerY);

  return canvas;
}

/**
 * Downloads the high-resolution PNG image directly to user device
 */
export async function downloadLifeCardPng(
  summary: LifeSummary,
  ratio: CardAspectRatio = '1:1'
): Promise<void> {
  const canvas = await generateLifeCardCanvas(summary, ratio);
  const dataUrl = canvas.toDataURL('image/png', 1.0);

  const cleanName = summary.characterName.replace(/\s+/g, '_');
  const filename = `KiepNguoi_${summary.country.code}_${cleanName}_${summary.deathAge}Tuoi_${ratio.replace(':', 'x')}.png`;

  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Shares the card via Telegram WebApp shareUrl or native share dialog
 */
export async function shareLifeCardTelegram(summary: LifeSummary): Promise<void> {
  const shareText = `Tôi vừa trải nghiệm trọn một cuộc đời tại ${summary.country.nameVi}, thọ ${summary.deathAge} tuổi (${summary.finalCareer})!\nKhám phá cuộc đời của bạn trên Telegram tại: https://t.me/Gamengayxua_bot?startapp=life_${summary.shareCode}`;
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(`https://t.me/Gamengayxua_bot?startapp=life_${summary.shareCode}`)}&text=${encodeURIComponent(shareText)}`;

  if (window.Telegram?.WebApp?.openTelegramLink) {
    window.Telegram.WebApp.openTelegramLink(shareUrl);
  } else {
    window.open(shareUrl, '_blank');
  }
}
